// Lint exception: We want it here
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { Html } from "@react-three/drei";
import { Tree } from "fluid-framework";
import { useEffect, useRef, useState } from "react";
import { Vector3 } from "three";
import type { Group, Object3DEventMap } from "three";

import { GeneralLabel } from "../../components/common/general-label.js";
import { LeaveOfficeAction } from "../../components/ui/actions/leave-office-action.js";
import { SeatedAvatarList } from "../../components/ui/office/avatars/seated-avatar-list.js";
import { UnseatedAvatarList } from "../../components/ui/office/avatars/unseated-avatar-list.js";
import { HoverBox } from "../../components/ui/office/hover-box.js";
import { useAvatarSeating } from "../../hooks/use-avatar-seating.js";
import { useBezonedContext } from "../../hooks/use-bezoned-context.js";
import { useHover } from "../../hooks/use-hover.js";
import {
  isUserInStartZone,
  userMoveToEntranceArea,
} from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useOffice } from "../office/use-office.js";

const CENTER_ELEVATION_OFFSET = 2;

export const StartZone = () => {
  const { ContextBridge } = useBezonedContext();
  const [shouldHover, setShouldHover] = useState(true);
  const groupRef = useRef<Group<Object3DEventMap> | null>(null);
  const hovered = useHover(groupRef, shouldHover);
  const { fluidUserId, sharedTree } = useFluid();
  const [isUserInEntranceArea, setIsUserInEntranceArea] = useState(false);
  const {
    preset: {
      manifest: { startZone },
    },
  } = useOffice();

  // Use the shared hook for user filtering - seatCount: 0 means all users go to overflow
  const { seatedUsers, overflowUsers } = useAvatarSeating({
    getParticipantIds: () => [...(sharedTree.root.entranceArea.users ?? [])],
    locationNode: sharedTree.root.entranceArea,
    seatCount: startZone.seating?.seats?.length ?? 0,
  });

  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.updateMatrixWorld();
    }
  }, []);

  useEffect(() => {
    const updateHoverState = () => {
      const isInStartZone = isUserInStartZone({
        sharedTree,
        userId: fluidUserId,
      });
      setIsUserInEntranceArea(isInStartZone);
      setShouldHover(!isInStartZone);
    };

    const unsubscribe = Tree.on(
      sharedTree.root.entranceArea,
      "treeChanged",
      () => {
        updateHoverState();
      }
    );

    updateHoverState();

    return () => {
      unsubscribe();
    };
  }, [fluidUserId, sharedTree]);

  const clickedStartZone = useStableCallback(
    (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      userMoveToEntranceArea({ sharedTree, userId: fluidUserId });
    }
  );

  return (
    <group
      onClick={clickedStartZone}
      position={
        new Vector3(
          startZone.position[0],
          startZone.position[1],
          startZone.position[2]
        )
      }
      ref={groupRef}
    >
      <GeneralLabel config={startZone.text} hovered={true} value="WELCOME" />

      <HoverBox config={startZone.hoverBox} hovered={hovered} />

      <SeatedAvatarList
        seats={
          startZone.seating?.seats?.map(
            (seat) => new Vector3(seat[0], seat[1], seat[2])
          ) ?? []
        }
        users={seatedUsers}
      />
      <UnseatedAvatarList
        offset={
          new Vector3(
            ...(startZone.seating?.listOffset ??
              startZone.seating?.seats?.at(-1) ?? [0, 0, 0])
          )
        }
        users={overflowUsers}
      />

      <Html
        center
        pointerEvents="none"
        position={new Vector3(0, CENTER_ELEVATION_OFFSET, 0)}
        sprite
        style={{
          pointerEvents: "none",
          userSelect: "none",
        }}
        transform
      >
        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            justifyContent: "center",
            pointerEvents: "auto",
          }}
        >
          <ContextBridge>
            {Boolean(isUserInEntranceArea) && <LeaveOfficeAction />}
          </ContextBridge>
        </div>
      </Html>
    </group>
  );
};
